import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { formatDate } from "../lib/format";
import UserBadge from "../components/UserBadge";
import {
  Trash2, RotateCcw, ClipboardList, Users, UserCog, Package, AlertTriangle,
} from "lucide-react";

const TYPE_META = {
  order:    { label: "Order",    icon: ClipboardList, color: "text-forest bg-forest-soft" },
  buyer:    { label: "Buyer",    icon: Users,         color: "text-terracotta bg-terracotta/10" },
  employee: { label: "Employee", icon: UserCog,       color: "text-blue-700 bg-blue-50" },
  product:  { label: "Product",  icon: Package,       color: "text-purple-700 bg-purple-50" },
};

const TABS = [
  { key: "all",      label: "All" },
  { key: "order",    label: "Orders" },
  { key: "buyer",    label: "Buyers" },
  { key: "employee", label: "Employees" },
  { key: "product",  label: "Products" },
];

export default function Trash() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [ttlDays, setTtlDays] = useState(45);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/trash");
      setItems(data.items || []);
      setTtlDays(data.ttl_days || 45);
      if (data.auto_purged > 0) {
        toast.info(`${data.auto_purged} item(s) auto-removed after ${data.ttl_days} days`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const restore = async (it) => {
    try {
      await api.post(`/trash/${it.type}/${it.id}/restore`);
      toast.success(`${TYPE_META[it.type]?.label || "Item"} restored`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const deleteForever = async (it) => {
    if (!window.confirm(`Permanently delete "${it.label}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/trash/${it.type}/${it.id}`);
      toast.success("Deleted forever");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const emptyAll = async () => {
    if (items.length === 0) return;
    if (!window.confirm(`Permanently delete ALL ${items.length} item(s) in the bin? This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/trash/empty");
      toast.success(`Emptied bin — ${data.deleted_forever} item(s) deleted forever`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const counts = items.reduce((m, it) => { m[it.type] = (m[it.type] || 0) + 1; return m; }, {});
  const filtered = tab === "all" ? items : items.filter((i) => i.type === tab);

  const goToOriginal = (it) => {
    if (it.type === "order")    nav(`/orders/${it.id}`);
    if (it.type === "buyer")    nav(`/parties/${it.id}`);
    if (it.type === "employee") nav(`/employees/${it.id}`);
  };

  return (
    <div data-testid="trash-page">
      <PageHeader
        title="Recycle Bin"
        subtitle={`Items you delete land here for ${ttlDays} days before they're permanently removed`}
        action={
          items.length > 0 ? (
            <button
              onClick={emptyAll}
              data-testid="trash-empty-btn"
              className="px-3 py-2 rounded-lg border border-red-200 text-red-700 font-figtree font-medium text-sm hover:bg-red-50 transition-colors duration-200 flex items-center gap-2"
            >
              <Trash2 className="h-4 w-4" /> Empty Bin
            </button>
          ) : null
        }
      />

      {/* Info banner */}
      <div className="card-surface p-4 mb-6 flex items-start gap-3 border-l-4 border-yellow-400">
        <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
        <div className="font-figtree text-sm text-ink">
          <strong>How the bin works:</strong> anything you delete from Orders, Buyers, Employees, or Products is kept here for <b>{ttlDays} days</b>. You can restore it any time in that window. After {ttlDays} days it's permanently removed and cannot be recovered.
        </div>
      </div>

      {/* Type tabs */}
      <div className="card-surface p-2 mb-6 flex flex-wrap gap-1">
        {TABS.map((t) => {
          const n = t.key === "all" ? items.length : (counts[t.key] || 0);
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`trash-tab-${t.key}`}
              className={`px-4 py-2 rounded-lg text-sm font-figtree font-medium transition-colors duration-200 ${
                tab === t.key ? "bg-forest text-white" : "text-ink-soft hover:bg-sand"
              }`}
            >
              {t.label} <span className="opacity-70">({n})</span>
            </button>
          );
        })}
      </div>

      <div className="card-surface overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-ink-soft font-figtree">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <div className="mx-auto h-12 w-12 rounded-xl bg-forest-soft text-forest grid place-items-center mb-3">
              <Trash2 className="h-6 w-6" />
            </div>
            <div className="font-outfit font-semibold text-ink">Nothing here</div>
            <div className="text-ink-soft text-sm font-figtree">
              {tab === "all" ? "Your recycle bin is empty." : `No ${TABS.find(x=>x.key===tab)?.label.toLowerCase()} in the bin.`}
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-sand/60">
                <tr>
                  <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-6 py-4">Type</th>
                  <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-6 py-4">Item</th>
                  <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-6 py-4">Deleted</th>
                  <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-6 py-4">By</th>
                  <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-6 py-4">Time left</th>
                  <th className="text-right font-figtree font-semibold text-sm text-ink-soft px-6 py-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => {
                  const meta = TYPE_META[it.type] || TYPE_META.order;
                  const Icon = meta.icon;
                  const urgent = (it.days_left ?? 999) <= 7;
                  return (
                    <tr
                      key={`${it.type}-${it.id}`}
                      data-testid={`trash-row-${it.type}-${it.id}`}
                      className="border-t border-line align-middle hover:bg-bone/60"
                    >
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-figtree font-semibold ${meta.color}`}>
                          <Icon className="h-3.5 w-3.5" /> {meta.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 font-figtree font-medium text-ink">
                        <button
                          onClick={() => goToOriginal(it)}
                          className="hover:underline text-left"
                          title={it.type === "product" ? "" : "Open original"}
                        >
                          {it.label}
                        </button>
                      </td>
                      <td className="px-6 py-4 font-figtree text-ink-soft text-sm whitespace-nowrap">{formatDate(it.deleted_at)}</td>
                      <td className="px-6 py-4">
                        <UserBadge user={it.deleted_by} />
                      </td>
                      <td className="px-6 py-4 font-figtree text-sm whitespace-nowrap">
                        <span className={`font-outfit font-semibold ${urgent ? "text-red-700" : "text-ink"}`}>
                          {it.days_left ?? "—"} days
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex justify-end items-center gap-1">
                          <button
                            onClick={() => restore(it)}
                            data-testid={`trash-restore-${it.type}-${it.id}`}
                            className="px-3 py-1.5 rounded-lg text-forest hover:bg-forest-soft text-sm font-figtree font-semibold flex items-center gap-1.5 transition-colors duration-200"
                          >
                            <RotateCcw className="h-4 w-4" /> Restore
                          </button>
                          <button
                            onClick={() => deleteForever(it)}
                            data-testid={`trash-purge-${it.type}-${it.id}`}
                            className="p-2 rounded-lg text-ink-soft hover:bg-red-50 hover:text-red-700 transition-colors duration-200"
                            title="Delete forever"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
